import { ColumnsType } from "antd/es/table";
import { useTranslation } from "react-i18next";
import { getTranslateNsT } from "@/utils/getTranslateNsT";
import { isInvoice, refundType } from "@/pages/payment/hooks/useMemberColumns";
import { dateFormatter } from "project-config/utils/date-formatter";
import { OperationButton } from "@/components/operation-button";
import { PaymentTypeCode } from "project-config/constants";
import request from "project-config/services/request/request";
import { getDomain } from "project-config/utils/domain";
import routerUtil from "@/utils/routerUtil";

const t = getTranslateNsT("center");

let statusTemplate = [
  t("pay_status_default"),
  t("pay_status_ing"),
  t("pay_status_success"),
  t("pay_status_cancel"),
  t("pay_status_fail"),
  t("pay_status_refund"),
  t("pay_status_wait_comfirm"),
  t("pay_status_comfirmed"),
  t("pay_status_present"),
];

let payMethodArr = [
  t("pay_method_ali"),
  t("pay_method_wechat"),
  t("pay_method_paypal"),
  t("pay_method_coupon"),
  t("pay_method_income_child"),
  t("pay_method_income_parent"),
  t("pay_method_outcome_child"),
  t("pay_method_outcome_parent"),
  t("pay_method_give_register"),
  t("pay_method_give_info"),
  t("pay_method_invoice_pre"),
  t("pay_method_invoice_after"),
  t("pay_method_outline"),
  t("pay_method_return_cash"),
  t("pay_method_return_err"),
  t("pay_method_give"),
  t("pay_method_old_balance_to_new"),
  t("pay_method_old_coupon_to_new"),
  t("pay_method_activity_give"),
  t("pay_method_agent_coupon"),
  t("pay_method_wechat_present"),
  t("pay_method_balance_adjust"),
  t("pay_method_mini_app"),
];

const IncomeOutcomeTemplate = [
  "",
  t("income_type"),
  t("outcome_type"),
  t("income_major_type"),
];

export function useNormalColumns({
  isSubAccount,
  isDomestic,
}: {
  isSubAccount: boolean;
  isDomestic: boolean;
}) {
  const { t } = useTranslation("center");

  const columns: ColumnsType<any> = [
    {
      dataIndex: "payRecordId",
      title: t("record_header_id"),
    },
    {
      dataIndex: "amount",
      title: t("record_header_amount"),
      render: (_, row) => {
        return Number(row.amount).toFixed(3);
      },
    },
    !getDomain()
      ? {
          dataIndex: "amountQy",
          title: t("record_header_youth_amount"),
          render: (_, row) => {
            return row.amountQy === null ? "" : Number(row.amountQy).toFixed(3);
          },
        }
      : {},
    {
      dataIndex: "balance",
      title: t("record_header_balance"),
      render: (_, row) => {
        let status = [2, 5, 7]; // 支付成功，退款，已确认收款 显示充值后余额
        return status.includes(row.statusId)
          ? Number(row.accountUpdate).toFixed(3)
          : "";
      },
    },
    {
      dataIndex: "statusId",
      title: t("record_header_status"),
      render: (_, row) => {
        if (row.statusId === 6) {
          return (
            <div>
              <span>{`${statusTemplate[row.statusId]}: `}</span>
              <span style={{ color: "#FF9933" }}>{row.nonPayment}</span>
            </div>
          );
        }
        return statusTemplate[row.statusId];
      },
    },
    {
      dataIndex: "payMethodId",
      title: t("record_header_method"),
      render: (_, row) => {
        if (
          [26, 30, 31, 37, 38, 39, 40, 41, 42, 43].includes(row.payMethodId)
        ) {
          return {
            26: t("pay_method_stripe"),
            30: payMethodArr[0],
            31: payMethodArr[1],
            37: t("pay_method_offline_qy"),
            38: payMethodArr[22],
            39: t("pay_method_recharge_adjust"),
            40: t("pay_method_consume_adjust"),
            41: payMethodArr[0],
            42: payMethodArr[1],
            43: t("pay_method_offline_gpu_qy"),
          }[row.payMethodId as number];
        }
        return row.payMethodId === 14
          ? refundType.get(row.refundMethodId) +
              payMethodArr[row.payMethodId - 1]
          : payMethodArr[row.payMethodId - 1];
      },
    },
    {
      dataIndex: "incomeOroutcome",
      title: t("record_header_type"),
      render: (_, row) => {
        return IncomeOutcomeTemplate[row.incomeOroutcome];
      },
    },
    {
      dataIndex: "rollInUsername",
      title: t("record_header_account"),
    },
    {
      dataIndex: "submitDate",
      title: t("record_header_submitdate"),
      render: (_, row) => {
        return dateFormatter(row.submitDate);
      },
    },
    !getDomain()
      ? {
          dataIndex: "isInvoice",
          title: t("record_header_invoice"),
          render: (_, row) => {
            return row.isInvoice === 2 ? (
              <>
                <span style={{ marginRight: "10px" }}>
                  {t("pay_invoicing")}
                </span>
              </>
            ) : (
              isInvoice[row.isInvoice]
            );
          },
        }
      : {},
    {
      dataIndex: "handle",
      title: t("record_header_operate"),
      render: (_, row) => {
        let state;
        const isPaypalBcdc = Number(row.isPaypalBcdc) === 1;

        if (
          row.statusId === 1 &&
          !isPaypalBcdc &&
          ![1, 2, 38, 41, 30, 31].includes(row.payMethodId)
        ) {
          state = "continuePay";
        }

        if (
          row.statusId === 2 &&
          [1, 2, 3, 11, 13, 26, 37, 43, 38].includes(row.payMethodId)
        ) {
          state = "pay";
        }

        if (row.statusId === 7 && [13, 26].includes(row.payMethodId)) {
          state = "pay";
        }

        const btnStateObj = {
          continuePay: {
            click: async () => {
              const payMethod = row.payMethodId;
              let blockPopup = null;
              if (
                payMethod === PaymentTypeCode.Alipay ||
                payMethod === PaymentTypeCode.Paypal ||
                payMethod === 30
              ) {
                blockPopup = window.open("about:blank", "_blank") as Window;
                if (payMethod === PaymentTypeCode.Paypal) {
                  blockPopup.document.body.innerHTML = `<p>Please wait while you are being redirected...</p>`;
                }
              }
              let ret = await request.post(
                "/api/rendering/pay/recharge/paySecond",
                { outTradeNo: row.payRecordId }
              );
              if (ret.code !== 200) {
                return;
              }
              if (payMethod === PaymentTypeCode.Alipay || payMethod === 30) {
                blockPopup!.document.body.innerHTML = ret.data;
                blockPopup!.document.getElementsByTagName("form")[0].submit();
              }
              if (payMethod === PaymentTypeCode.Wechat || payMethod === 31) {
                // let QrcodeImgSrc = "data:image/png;base64," + ret.data;
                // todo Modal show Qrcode
              }
              if (row.payMethodId === 3) {
                blockPopup!.location = ret.data;
              }
            },
            btn: "record_header_operate_continue_pay",
          },
          pay: {
            click: () => {
              function createFormToDownload(
                url: string,
                params: Record<string, any>
              ) {
                let b = document.getElementsByTagName("body"),
                  i = "download" + Date.now(),
                  s = document.createElement("form");
                s.setAttribute("id", i);
                s.setAttribute("method", "post");
                s.setAttribute("action", url);
                s.setAttribute("target", "_blank");
                for (let k in params) {
                  let input = document.createElement("input");
                  input.setAttribute("name", k);
                  input.setAttribute("value", params[k]);
                  s.appendChild(input);
                }

                if (b && b[0]) {
                  b[0].appendChild(s);
                }
                s.submit();
                setTimeout(() => {
                  s.remove();
                }, 1600);
              }

              createFormToDownload(
                "/api/rendering/user/export/exportRechargePdf",
                { rechargeId: row.payRecordId }
              );
            },
            btn: "record_header_operate_download_order",
          },
        }[state ?? ""];

        if (btnStateObj) {
          return (
            <>
              <OperationButton
                onClick={btnStateObj.click}
                text={t(btnStateObj.btn)}
              />
              {row.isInvoice === 2 && isDomestic && !isSubAccount && (
                <OperationButton
                  onClick={() => routerUtil.navigate?.("/invoice")}
                  text={isInvoice[row.isInvoice]}
                />
              )}
            </>
          );
        }
      },
    },
  ];

  return { columns };
}
